/**
 * Help content for the Rules tab — "How it works" with concrete examples.
 *
 * For Media Devoted | Created By Ciro Autiero
 *
 * Rewritten 2026-08: the previous copy claimed "today the action is Exclude"
 * and told the reader to "switch tabs to edit a different scope" — both left
 * over from the per-layer tab model. Six actions ship and one rule spans layers.
 */

import { FC } from 'react';

import { Help } from '../../components/help';

const ExcludeHelpContent: FC = () => (
  <>
    <Help.Section title="What a rule is">
      <p>
        Each rule reads as a sentence: <Help.Inline>on</Help.Inline> some layers,
        {' '}<Help.Inline>when</Help.Inline> its conditions match,
        {' '}<Help.Inline>then</Help.Inline> its actions run — on every optimizer
        update, against whatever is live at that moment.
      </p>
    </Help.Section>

    <Help.Section title="Match ALL vs ANY">
      <p>
        <Help.Inline>ALL</Help.Inline> means every condition must be true
        {' '}(<code className="font-mono">AND</code>);{' '}
        <Help.Inline>ANY</Help.Inline> means one is enough
        {' '}(<code className="font-mono">OR</code>). The connector between the
        rows shows which is in force. <Help.Inline>NOT</Help.Inline> inverts
        whatever sits inside it.
      </p>
    </Help.Section>

    <Help.Section title="What the actions do">
      <Help.Example
        pattern="Exclude"
        effect="stays live, but the optimizer stops re-weighting it"
      />
      <Help.Example
        pattern="Weight — Static pin"
        effect="all matches together get a fixed % of their pool (split by score); the rest share the remainder. Pins that collide over 100% block the preview until fixed"
      />
      <Help.Example
        pattern="Weight — Dynamic boost"
        effect="all matches together share +N points on top of their computed weights; scaled down automatically when it can't fit"
      />
      <Help.Example
        pattern="Set to queue"
        effect="off the tracker, back into the queue line"
      />
      <Help.Example
        pattern="Eliminate"
        effect="off the tracker for good — the bank keeps the row"
      />
      <Help.Example
        pattern="+ Add new element"
        effect="makes it a SWAP: nothing leaves unless the queue has a replacement ready"
      />
    </Help.Section>

    <Help.Section title="Examples">
      <Help.Example pattern="roi < 0" effect="freeze losing items so the optimizer stops feeding them" />
      <Help.Example pattern="uniqueClicks < 20" effect="protect new items with too little data" />
      <Help.Example
        pattern="tag TRASH · Eliminate + Add new element"
        effect="retire anything tagged TRASH and pull the next path from the bank"
      />
    </Help.Section>

    <Help.Section title="Capacity, above">
      <p>
        A rule decides <Help.Inline>what leaves</Help.Inline>; capacity decides
        {' '}<Help.Inline>how many may be live</Help.Inline> at once. Anything
        over a cap is moved back to the queue on the next update — no rule
        needed.
      </p>
    </Help.Section>
  </>
);

export default ExcludeHelpContent;
